/**
 * The values we publish about a part, under our single custom key.
 *
 * All three appear on every product row we touch — ours and the sources' alike —
 * so this panel is where an operator sees exactly what a customer will see, and
 * edits the one part of it that is written rather than computed.
 */
import { useEffect, useState } from 'react';
import { useApi } from '../lib/api';
import type { ProductCustomValues } from '../lib/types';
import { Chip, Field, SourceChip } from '../ui';

/**
 * The scale runs the other way from a rating: 0 is good and 5 is "confirmed
 * wrong". Green at nothing-reported, red at a confirmed error.
 */
function scoreClass(score: number): string {
  if (score === 0) return 'text-ok';
  if (score >= 4) return 'text-bad';
  return 'text-warn';
}

export default function ProductCustom({
  productId,
  values,
  onChange,
}: {
  productId: number;
  values: ProductCustomValues | null;
  onChange: () => void;
}) {
  const { request } = useApi();
  const [text, setText] = useState(values?.description ?? '');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setText(values?.description ?? '');
  }, [values?.description]);

  const dirty = text !== (values?.description ?? '');

  async function save() {
    setBusy(true);
    setSaved(false);
    await request('PUT', `/v1/admin/products/${productId}/description`, {
      description: text.trim() ? text : null,
    });
    setBusy(false);
    setSaved(true);
    onChange();
  }

  const ac = values?.anticross;
  const score = ac?.score ?? 0;

  return (
    <div className="flex flex-col gap-3">
      <Field label="Description">
        <textarea
          className="input"
          rows={3}
          placeholder="What a customer should know about this part — fitment, a caveat, a supersession…"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </Field>
      <div className="flex items-center gap-2">
        <button className="btn btn-sm btn-primary" disabled={busy || !dirty} onClick={() => void save()}>
          Save description
        </button>
        {saved && !dirty && <span className="text-[12px] text-ok">Saved.</span>}
        <span className="ml-auto text-[11.5px] text-faint">
          Published on every response that carries this product.
        </span>
      </div>

      <div className="flex flex-wrap items-start gap-6 border-t border-line pt-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-muted">Anticross</div>
          <div className={`num text-[22px] font-semibold ${scoreClass(score)}`}>
            {score > 0 && <span title="somebody has flagged this match">! </span>}
            {score}
            <span className="text-[13px] font-normal text-faint"> / 5</span>
          </div>
          <div className="text-[11.5px] text-faint">
            {score === 0
              ? 'nobody has reported this match as wrong'
              : `${ac!.status} · ${ac!.flagged} of ${ac!.oeLinks} OE links flagged`}
          </div>
        </div>

        <div>
          <div className="mb-1 text-[11px] uppercase tracking-wider text-muted">Stated by</div>
          <div className="flex flex-wrap gap-1">
            {(values?.sources ?? []).length === 0 ? (
              <span className="text-[12px] text-faint">—</span>
            ) : (
              values!.sources.map((s) => <SourceChip key={s} source={s} />)
            )}
          </div>
        </div>
      </div>

      {ac && ac.rules.length > 0 && (
        <div className="rounded border border-line bg-panel2 p-2">
          <div className="mb-1 text-[11.5px] text-faint">
            Why — the score is the strongest of these, so one confirmed report is not diluted by weaker doubts:
          </div>
          {ac.rules.map((r) => (
            <div key={`${r.oe}-${r.status}`} className="flex items-baseline gap-2 text-[12px]">
              <Chip className={r.status === 'confirmed' ? 'border-bad text-bad' : 'border-warn text-warn'}>
                {r.status}
              </Chip>
              <span className="mono">{r.oe}</span>
              <span className="text-faint">{r.reason ?? ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
